import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Loader2, Download, Share2, X, Car, Camera, Heart } from "lucide-react";
import { RB100_LOGO } from "../lib/api";

const TIER_META = {
  car_owner: {
    label: "مالك سيارة",
    badge: "CAR OWNER",
    icon: Car,
    accent: "#E60012", // RB red
  },
  photographer: {
    label: "مصوّر النادي",
    badge: "PHOTOGRAPHER",
    icon: Camera,
    accent: "#D4AF37", // gold
  },
  fan: {
    label: "هاوي ومتابع",
    badge: "CLUB FAN",
    icon: Heart,
    accent: "#E60012",
  },
};

/**
 * ShareCard — generates a beautiful 1080x1080 PNG of the membership for
 * social-media sharing. Uses tier-specific designs (option d).
 */
export default function ShareCard({ data, onClose }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState(null);

  // Pre-fetch the logo as data URL so html-to-image can embed it
  // without CORS issues during PNG generation.
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(RB100_LOGO, { mode: "cors" });
        const blob = await res.blob();
        const dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(blob);
        });
        if (!aborted) setLogoDataUrl(dataUrl);
      } catch (e) {
        // Fallback: use original URL — display still works for preview
        if (!aborted) setLogoDataUrl(RB100_LOGO);
      }
    })();
    return () => { aborted = true; };
  }, []);

  const tier = TIER_META[data.membership_type || "car_owner"] || TIER_META.car_owner;

  const generateBlob = async () => {
    if (!cardRef.current) return null;
    const dataUrl = await toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#000000",
      skipFonts: true, // we use system fonts — avoid Google-Fonts CORS issues
    });
    const res = await fetch(dataUrl);
    return await res.blob();
  };

  const downloadImage = async () => {
    setBusy(true);
    try {
      const blob = await generateBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RB100-عضوية-${data.reference_code}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("تم تحميل البطاقة ✓");
    } catch (e) {
      console.error(e);
      toast.error("تعذّر إنشاء الصورة. جرّب مرة أخرى.");
    } finally {
      setBusy(false);
    }
  };

  const shareImage = async () => {
    setBusy(true);
    try {
      const blob = await generateBlob();
      if (!blob) return;
      const file = new File([blob], `RB100-${data.reference_code}.png`, {
        type: "image/png",
      });
      const shareData = {
        title: "بطاقة عضويتي في نادي RB100",
        text: `أنا الآن عضو رسمي في نادي RB100 — السيارات الكلاسيكية والمعدّلة في المدينة المنورة 🏁\nrb100.net`,
        files: [file],
      };
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        // Desktop fallback: download
        await downloadImage();
        return;
      }
      toast.success("تمت المشاركة ✓");
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error(e);
        toast.error("تعذّر المشاركة. جرّب التحميل بدلاً من ذلك.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
         data-testid="share-card-modal"
         onClick={onClose}>
      <div className="relative max-w-[560px] w-full" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          data-testid="share-card-close"
          className="absolute -top-12 left-0 text-white/80 hover:text-white flex items-center gap-2 text-sm"
        >
          <X className="w-5 h-5" />
          إغلاق
        </button>

        {/* The actual card to be exported (1080x1080 logical, scaled to fit screen) */}
        <div
          className="bg-black overflow-hidden mx-auto"
          style={{ width: "518px", height: "518px" }}
        >
          <div
            ref={cardRef}
            style={{
              width: "1080px",
              height: "1080px",
              transform: "scale(0.4796)",
              transformOrigin: "top right",
              direction: "rtl",
            }}
          >
            <CardArtwork data={data} tier={tier} logo={logoDataUrl} />
          </div>
        </div>

        {/* Action buttons */}
        <div className="bg-white p-5 grid grid-cols-2 gap-3 mt-4 mx-auto" style={{ maxWidth: "518px" }}>
          <button
            onClick={shareImage}
            disabled={busy}
            data-testid="share-card-share-btn"
            className="bg-rb-red hover:bg-rb-redHover disabled:opacity-60 text-white font-bold py-3 px-5 transition-colors flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5" />}
            مشاركة
          </button>
          <button
            onClick={downloadImage}
            disabled={busy}
            data-testid="share-card-download-btn"
            className="border-2 border-rb-ink hover:bg-rb-ink hover:text-white text-rb-ink disabled:opacity-60 font-bold py-3 px-5 transition-colors flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            تحميل الصورة
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   The 1080x1080 artwork — switches by tier (option d, 3 styles)
   ============================================================ */
const CardArtwork = ({ data, tier, logo }) => {
  const TierIcon = tier.icon;

  if (data.membership_type === "photographer") {
    return <PhotographerCard data={data} tier={tier} TierIcon={TierIcon} logo={logo} />;
  }
  if (data.membership_type === "fan") {
    return <FanCard data={data} tier={tier} TierIcon={TierIcon} logo={logo} />;
  }
  return <CarOwnerCard data={data} tier={tier} TierIcon={TierIcon} logo={logo} />;
};

/* ---------- Style 1: Car Owner — Black + red, racing stripes ---------- */
const CarOwnerCard = ({ data, tier, TierIcon, logo }) => (
  <div
    style={{
      width: "1080px",
      height: "1080px",
      background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)",
      color: "#fff",
      fontFamily: '"Tajawal", "Changa", system-ui, sans-serif',
      position: "relative",
      overflow: "hidden",
      padding: "60px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    {/* Diagonal red racing stripes */}
    <div
      style={{
        position: "absolute",
        top: "-50px",
        right: "-200px",
        width: "1400px",
        height: "180px",
        background: "#E60012",
        transform: "rotate(-12deg)",
        opacity: 0.95,
      }}
    />
    <div
      style={{
        position: "absolute",
        bottom: "120px",
        right: "-250px",
        width: "1400px",
        height: "8px",
        background: "#E60012",
        transform: "rotate(-12deg)",
        opacity: 0.4,
      }}
    />
    <div
      style={{
        position: "absolute",
        bottom: "100px",
        right: "-250px",
        width: "1400px",
        height: "3px",
        background: "#E60012",
        transform: "rotate(-12deg)",
        opacity: 0.7,
      }}
    />

    {/* Header */}
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 2 }}>
      <div>
        <div style={{ fontSize: "16px", letterSpacing: "8px", color: "#fff", fontWeight: 700, marginTop: "30px" }}>
          {tier.badge}
        </div>
        <div style={{ fontSize: "20px", marginTop: "8px", color: "rgba(255,255,255,0.7)" }}>
          {tier.label} — RB100
        </div>
      </div>
      <div style={{ background: "#fff", padding: "12px", width: "120px", height: "120px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {logo && <img src={logo} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
      </div>
    </div>

    {/* Body — name + car */}
    <div style={{ position: "relative", zIndex: 2, marginTop: "60px" }}>
      <div style={{ fontSize: "22px", letterSpacing: "10px", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", marginBottom: "20px" }}>
        عضو رسمي
      </div>
      <div style={{ fontSize: "92px", fontWeight: 900, lineHeight: 1, marginBottom: "30px", letterSpacing: "-2px" }}>
        {data.name}
      </div>

      {(data.car_make || data.car_model || data.car_year) && (
        <div style={{ borderTop: "2px solid rgba(255,255,255,0.15)", paddingTop: "25px", marginTop: "20px" }}>
          <div style={{ fontSize: "20px", color: "rgba(255,255,255,0.55)", letterSpacing: "6px", marginBottom: "10px" }}>
            السيارة
          </div>
          <div style={{ fontSize: "48px", fontWeight: 800, color: "#fff" }}>
            {[data.car_year, data.car_make, data.car_model].filter(Boolean).join(" · ")}
          </div>
          {data.plate_number && (
            <div style={{ fontSize: "32px", fontFamily: "'Courier New', monospace", color: "#E60012", fontWeight: 700, letterSpacing: "8px", marginTop: "16px" }}>
              {data.plate_number}
            </div>
          )}
        </div>
      )}
    </div>

    {/* Footer */}
    <div style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "flex-end", justifyContent: "space-between", borderTop: "2px solid rgba(255,255,255,0.15)", paddingTop: "25px" }}>
      <div>
        <div style={{ fontSize: "16px", color: "rgba(255,255,255,0.5)", letterSpacing: "6px", marginBottom: "8px" }}>
          رقم العضوية
        </div>
        <div style={{ fontSize: "32px", fontFamily: "'Courier New', monospace", color: "#fff", fontWeight: 700, letterSpacing: "4px" }}>
          {data.reference_code}
        </div>
      </div>
      <div style={{ textAlign: "left" }}>
        <div style={{ fontSize: "16px", color: "rgba(255,255,255,0.5)", letterSpacing: "4px" }}>
          المدينة المنورة
        </div>
        <div style={{ fontSize: "22px", color: "#E60012", fontWeight: 700, marginTop: "6px" }}>
          منذ ٢٠٠٢ · rb100.net
        </div>
      </div>
    </div>
  </div>
);

/* ---------- Style 2: Photographer — Gold accents, dark, frame motif ---------- */
const PhotographerCard = ({ data, tier, TierIcon, logo }) => (
  <div
    style={{
      width: "1080px",
      height: "1080px",
      background: "linear-gradient(160deg, #0d0d0d 0%, #1a1410 50%, #0a0a0a 100%)",
      color: "#fff",
      fontFamily: '"Tajawal", "Changa", system-ui, sans-serif',
      position: "relative",
      overflow: "hidden",
      padding: "60px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    {/* Gold corners — camera viewfinder style */}
    {[
      { top: 40, right: 40 },
      { top: 40, left: 40 },
      { bottom: 40, right: 40 },
      { bottom: 40, left: 40 },
    ].map((pos, i) => (
      <div key={i} style={{ position: "absolute", ...pos, width: "60px", height: "60px" }}>
        <div style={{ position: "absolute", top: 0, left: 0, width: "60px", height: "4px", background: "#D4AF37" }} />
        <div style={{ position: "absolute", top: 0, left: 0, width: "4px", height: "60px", background: "#D4AF37" }} />
      </div>
    ))}

    {/* Subtle grain */}
    <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 30% 20%, rgba(212,175,55,0.08), transparent 60%)" }} />

    {/* Header */}
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 2, marginTop: "30px" }}>
      <div>
        <div style={{ fontSize: "16px", letterSpacing: "8px", color: "#D4AF37", fontWeight: 700 }}>
          {tier.badge}
        </div>
        <div style={{ fontSize: "20px", marginTop: "8px", color: "rgba(255,255,255,0.7)" }}>
          {tier.label} · RB100
        </div>
      </div>
      <div style={{ background: "#fff", padding: "12px", width: "120px", height: "120px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {logo && <img src={logo} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
      </div>
    </div>

    {/* Body */}
    <div style={{ position: "relative", zIndex: 2, textAlign: "center" }}>
      {/* Camera icon backdrop */}
      <div style={{
        width: "180px", height: "180px", border: "4px solid #D4AF37",
        borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto 40px",
      }}>
        <div style={{ fontSize: "84px", color: "#D4AF37" }}>📸</div>
      </div>

      <div style={{ fontSize: "22px", letterSpacing: "10px", color: "rgba(212,175,55,0.8)", textTransform: "uppercase", marginBottom: "20px" }}>
        عدسة النادي الرسمية
      </div>
      <div style={{ fontSize: "88px", fontWeight: 900, lineHeight: 1, marginBottom: "40px", letterSpacing: "-2px" }}>
        {data.name}
      </div>

      <div style={{ display: "flex", justifyContent: "center", gap: "50px", flexWrap: "wrap" }}>
        {data.instagram_handle && (
          <div>
            <div style={{ fontSize: "16px", color: "rgba(255,255,255,0.5)", letterSpacing: "4px", marginBottom: "6px" }}>
              INSTAGRAM
            </div>
            <div style={{ fontSize: "30px", fontWeight: 700, color: "#D4AF37" }}>
              @{data.instagram_handle}
            </div>
          </div>
        )}
        {data.snapchat_handle && (
          <div>
            <div style={{ fontSize: "16px", color: "rgba(255,255,255,0.5)", letterSpacing: "4px", marginBottom: "6px" }}>
              SNAPCHAT
            </div>
            <div style={{ fontSize: "30px", fontWeight: 700, color: "#D4AF37" }}>
              @{data.snapchat_handle}
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Footer */}
    <div style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "flex-end", justifyContent: "space-between", borderTop: "2px solid rgba(212,175,55,0.3)", paddingTop: "25px" }}>
      <div>
        <div style={{ fontSize: "16px", color: "rgba(255,255,255,0.5)", letterSpacing: "6px", marginBottom: "8px" }}>
          رقم العضوية
        </div>
        <div style={{ fontSize: "32px", fontFamily: "'Courier New', monospace", color: "#fff", fontWeight: 700, letterSpacing: "4px" }}>
          {data.reference_code}
        </div>
      </div>
      <div style={{ textAlign: "left" }}>
        <div style={{ fontSize: "16px", color: "rgba(255,255,255,0.5)", letterSpacing: "4px" }}>
          المدينة المنورة
        </div>
        <div style={{ fontSize: "22px", color: "#D4AF37", fontWeight: 700, marginTop: "6px" }}>
          منذ ٢٠٠٢ · rb100.net
        </div>
      </div>
    </div>
  </div>
);

/* ---------- Style 3: Fan — Bold red with white text, energetic ---------- */
const FanCard = ({ data, tier, TierIcon, logo }) => (
  <div
    style={{
      width: "1080px",
      height: "1080px",
      background: "linear-gradient(135deg, #B30000 0%, #E60012 50%, #B30000 100%)",
      color: "#fff",
      fontFamily: '"Tajawal", "Changa", system-ui, sans-serif',
      position: "relative",
      overflow: "hidden",
      padding: "60px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    {/* Big watermark RB100 number */}
    <div style={{
      position: "absolute",
      bottom: "-100px",
      left: "-100px",
      fontSize: "780px",
      fontWeight: 900,
      color: "rgba(255,255,255,0.08)",
      lineHeight: 1,
      letterSpacing: "-30px",
    }}>
      100
    </div>

    {/* Header */}
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 2, marginTop: "30px" }}>
      <div>
        <div style={{ fontSize: "16px", letterSpacing: "8px", color: "#fff", fontWeight: 700 }}>
          {tier.badge}
        </div>
        <div style={{ fontSize: "20px", marginTop: "8px", color: "rgba(255,255,255,0.85)" }}>
          {tier.label} · RB100
        </div>
      </div>
      <div style={{ background: "#fff", padding: "12px", width: "120px", height: "120px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {logo && <img src={logo} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
      </div>
    </div>

    {/* Body */}
    <div style={{ position: "relative", zIndex: 2, marginTop: "40px" }}>
      <div style={{ display: "inline-block", background: "#000", color: "#fff", padding: "10px 24px", fontSize: "20px", letterSpacing: "8px", fontWeight: 700, marginBottom: "30px" }}>
        OFFICIAL FAN
      </div>
      <div style={{ fontSize: "30px", color: "rgba(255,255,255,0.9)", marginBottom: "20px" }}>
        أنا الآن جزء من عائلة
      </div>
      <div style={{ fontSize: "100px", fontWeight: 900, lineHeight: 1, marginBottom: "20px", letterSpacing: "-2px" }}>
        {data.name}
      </div>
      <div style={{ fontSize: "32px", color: "rgba(255,255,255,0.95)", fontWeight: 600, marginTop: "30px", maxWidth: "780px", lineHeight: 1.4 }}>
        نادي RB100 — كلاسيك، معدّلة، وشغف لا ينتهي 🏁
      </div>
    </div>

    {/* Footer */}
    <div style={{ position: "relative", zIndex: 2, display: "flex", alignItems: "flex-end", justifyContent: "space-between", borderTop: "2px solid rgba(255,255,255,0.3)", paddingTop: "25px" }}>
      <div>
        <div style={{ fontSize: "16px", color: "rgba(255,255,255,0.7)", letterSpacing: "6px", marginBottom: "8px" }}>
          رقم العضوية
        </div>
        <div style={{ fontSize: "32px", fontFamily: "'Courier New', monospace", color: "#000", fontWeight: 700, letterSpacing: "4px", background: "#fff", padding: "8px 16px", display: "inline-block" }}>
          {data.reference_code}
        </div>
      </div>
      <div style={{ textAlign: "left" }}>
        <div style={{ fontSize: "16px", color: "rgba(255,255,255,0.7)", letterSpacing: "4px" }}>
          المدينة المنورة
        </div>
        <div style={{ fontSize: "22px", color: "#000", fontWeight: 800, marginTop: "6px", background: "#fff", padding: "4px 12px", display: "inline-block" }}>
          منذ ٢٠٠٢ · rb100.net
        </div>
      </div>
    </div>
  </div>
);
